import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { Box, CircularProgress, Divider, TextField, Typography } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import RewardsAPI from 'api/faber/RewardsAPI'
import { BootstrapDialog, BootstrapDialogTitle } from 'common/AbstractModal/BootstrapDialog'
import { RoundedButton } from 'common/Button/Button'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'

interface UserPlanDialogProps {
  customerWinbackDialogOpen: boolean
  userId: string
  handleCustomerWinbackDialogClose: () => void
}

interface CatalogRow {
  id: string
  name: string
  description: string
  tags: string[]
  show_client: boolean
}

export default function CustomerWinbackDialog({
  userId,
  customerWinbackDialogOpen,
  handleCustomerWinbackDialogClose
}: UserPlanDialogProps) {
  const queryClient = useQueryClient();
  const faberToken = OrbitSession.get('faberToken')
  const [paymentIntent, setPaymentIntent] = useState('');
  const [quickGlobalID, setQuickGlobalID] = useState('');

  const { data: catalogIds } = useQuery<CatalogRow[]>(['getCatalogs'], async () =>
    RewardsAPI.getCatalogs(faberToken)
  )

  useEffect(() => {
    if (catalogIds) {
      catalogIds.forEach((row) => {
        if (row.name == "QuickGlobal 2GB") {
          setQuickGlobalID(row.id)
        }
      })
    }
  }, [catalogIds])

  const { mutate: createUserPlan, isLoading: isRefreshNetworkLoading } =
    // Add bonus plan
    useMutation(OneTimePackApi.createUserPlan, {
      onSuccess: () => {
        toast.success(`Added 2GB QuickGlobal for the plan ${paymentIntent}`)
        queryClient.refetchQueries({ queryKey: ['getAllInactiveAndTerminatedOtpackUserPlans'] })
      },
      onError: () => {
        toast.error(
          `Error in adding 2GB QuickGlobal plan`
        )
      },
      onSettled: () => {
        handleCustomerWinbackDialogClose()
      }
    })

  const handleConfirm = () => {
    // sanity check
    if (quickGlobalID == "") {
      toast.error("Fetching catalog ids, please wait a moment and try again")
      return
    }

    const allValues = {
      userId: userId,
      // catalogId: "968649583414214657", // 2GB QuickGlobal, prod and staging is the same
      catalogId: quickGlobalID, // 2GB QuickGlobal, prod and staging is the same
      paymentIntent: paymentIntent,
      faberToken: faberToken
    }
    createUserPlan(allValues)
  }

  //handle form submit api here
  return (
    <div>
      <BootstrapDialog
        fullWidth
        onClose={() => handleCustomerWinbackDialogClose()}
        aria-labelledby='customized-dialog-title'
        open={customerWinbackDialogOpen}
      >
        <BootstrapDialogTitle onClose={() => handleCustomerWinbackDialogClose()}>
          Add Customer 2GB QuickGlobal Winback
        </BootstrapDialogTitle>

        <Divider sx={{ width: '100%' }} />

        <DialogContent>
          <NormalText sx={{ p: 0 }}>
            This will take the customers payment intent that we want to add the bonus plan to.
            This will add a 2GB QuickGlobal plan.
          </NormalText>
        </DialogContent>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, padding: 3 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>Payment Intent:</Typography>
          <TextField
            fullWidth
            label="Payment Intent"
            variant="outlined"
            value={paymentIntent}
            onChange={(e) => setPaymentIntent(e.target.value)}
            size='small'
          />
        </Box>

        <DialogActions sx={{ mb: '1rem' }}>
          <RoundedButton onClick={handleCustomerWinbackDialogClose}>
            Cancel
          </RoundedButton>
          <LoadingButton
            loading={isRefreshNetworkLoading}
            variant='filled'
            onClick={handleConfirm}
            loadingIndicator={<CircularProgress color='inherit' size={16} />}
            autoFocus
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </BootstrapDialog>
    </div>
  )
}
